export type WorkOrderStartSource = 'Manual' | 'Timesheet' | 'Not Started'
export type WorkOrderStartStatus = 'Late' | 'On Time' | 'Not Started'

interface ResolveWorkOrderStartData {
	scheduledTime?: number | null
	manualActualStartTime?: number | null
	timesheetActualStartTime?: number | null
	thresholdMinutes?: number
}

interface WorkOrderPermissionData {
	module: 'Contract' | 'Rental'
	currentUserUID?: string
	permissions?: { [key: string]: boolean }
	assignedTo?: { [key: string]: boolean } | string[] | string
	assignedToForeman?: { [key: string]: boolean } | string[] | string
}

export interface WorkOrderStartPerformance {
	source: WorkOrderStartSource
	status: WorkOrderStartStatus
	effectiveActualStartTime?: number
	delayMinutes: number
}

export const resolveEffectiveActualStartTime = ({
	manualActualStartTime,
	timesheetActualStartTime,
}: ResolveWorkOrderStartData) => {
	if (typeof manualActualStartTime === 'number') {
		return manualActualStartTime
	}
	if (typeof timesheetActualStartTime === 'number') {
		return timesheetActualStartTime
	}
	return undefined
}

export const resolveWorkOrderStartSource = ({
	manualActualStartTime,
	timesheetActualStartTime,
}: ResolveWorkOrderStartData): WorkOrderStartSource => {
	if (typeof manualActualStartTime === 'number') {
		return 'Manual'
	}
	if (typeof timesheetActualStartTime === 'number') {
		return 'Timesheet'
	}
	return 'Not Started'
}

export const getWorkOrderStartPerformance = (
	data: ResolveWorkOrderStartData,
): WorkOrderStartPerformance => {
	const { scheduledTime, thresholdMinutes = 0 } = data
	const source = resolveWorkOrderStartSource(data)
	const effectiveActualStartTime = resolveEffectiveActualStartTime(data)

	if (typeof scheduledTime !== 'number' || typeof effectiveActualStartTime !== 'number') {
		return {
			source,
			status: 'Not Started',
			effectiveActualStartTime,
			delayMinutes: 0,
		}
	}

	const delayMs = effectiveActualStartTime - scheduledTime
	const thresholdMs = thresholdMinutes * 60 * 1000
	if (delayMs > thresholdMs) {
		return {
			source,
			status: 'Late',
			effectiveActualStartTime,
			delayMinutes: Math.max(0, Math.ceil(delayMs / 60000)),
		}
	}

	return {
		source,
		status: 'On Time',
		effectiveActualStartTime,
		delayMinutes: 0,
	}
}

export const canEditWorkOrderActualStart = ({
	module,
	currentUserUID,
	permissions,
	assignedTo,
	assignedToForeman,
}: WorkOrderPermissionData) => {
	if (!currentUserUID) {
		return false
	}

	const allowedPermissionKeys =
		module === 'Contract'
			? ['Contracts_Manager', 'Contracts_Accounting', 'Contracts_Admin']
			: ['Rentals_Manager', 'Rentals_Accounting', 'Rentals_Admin']

	if (permissions && allowedPermissionKeys.some((key) => permissions[key])) {
		return true
	}

	const hasUserAssignment = (
		assignment?: { [key: string]: boolean } | string[] | string,
	) => {
		if (!assignment) {
			return false
		}
		if (Array.isArray(assignment)) {
			return assignment.includes(currentUserUID)
		}
		if (typeof assignment === 'string') {
			return assignment === currentUserUID
		}
		return Boolean(assignment[currentUserUID])
	}

	return hasUserAssignment(assignedTo) || hasUserAssignment(assignedToForeman)
}
